import { css } from "@emotion/css";
import { Callout, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentV2Resource, PersistenceSettings } from "@octopusdeploy/octopus-server-client";
import { ControlType, unbindParameterName, Permission, ProcessType } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { ProcessTemplatesParameterSelector } from "~/areas/platformHub/processEditor/ProcessTemplatesParameterSelector";
import { ChipIcon, ContextualMissingChip, EnvironmentChip } from "~/components/Chips";
import Lookup from "~/components/Lookup";
import { EnvironmentV2MultiSelect } from "~/components/MultiSelect/EnvironmentV2MultiSelect";
import { Note, Summary } from "~/components/form";
import ExpandableFormSection from "~/components/form/Sections/ExpandableFormSection";
import { EnvironmentOption } from "../types";
import { isVersionControlledProcess } from "./CommonProcessHelpers";
interface EnvironmentProps {
    environmentOption: EnvironmentOption;
    hasHiddenEnvironments: boolean;
    environments: EnvironmentV2Resource[];
    inclusiveEnvironments: string[];
    inclusiveEnvironmentsVariable: string | null;
    exclusiveEnvironments: string[];
    exclusiveEnvironmentsVariable: string | null;
    onEnvironmentOptionChanged(val: EnvironmentOption): void;
    onInclusiveEnvironmentsChanged(environments: string[]): void;
    onExclusiveEnvironmentsChanged(environments: string[]): void;
    onInclusiveEnvironmentsParameterChanged(environments: string): void;
    onExclusiveEnvironmentsParameterChanged(environments: string): void;
    onInclusiveEnvironmentRemoved?: (environment: string) => void;
    onExclusiveEnvironmentRemoved?: (environment: string) => void;
    persistenceSettings: PersistenceSettings;
    processType: ProcessType;
}
type Props = EnvironmentProps;
export class Environments extends React.Component<Props> {
    get isVersionControlled(): boolean {
        return isVersionControlledProcess(this.props.persistenceSettings, this.props.processType);
    }
    shouldComponentUpdate(nextProps: Props) {
        return nextProps.environmentOption !== this.props.environmentOption || nextProps.inclusiveEnvironments !== this.props.inclusiveEnvironments || nextProps.exclusiveEnvironments !== this.props.exclusiveEnvironments;
    }
    render() {
        return (<ExpandableFormSection title="Environments" help="Choose which environments this step applies to." summary={this.environmentSummary()} errorKey="environments">
                <RadioButtonGroup value={this.props.environmentOption} onChange={this.props.onEnvironmentOptionChanged}>
                    <RadioButton value={EnvironmentOption.All} label="Run for any environment" disabled={!this.isVersionControlled && this.props.hasHiddenEnvironments} isDefault={true}/>

                    <RadioButton value={EnvironmentOption.Include} label="Run only for specific environments" disabled={!this.isVersionControlled && this.props.hasHiddenEnvironments}/>
                    {this.props.environmentOption === EnvironmentOption.Include && (<div>
                            {this.props.processType === ProcessType.ProcessTemplate ? (<div className={styles.environmentsParameterStyles}>
                                    <ProcessTemplatesParameterSelector onParameterChange={this.props.onInclusiveEnvironmentsParameterChanged} selectedParameter={this.props.inclusiveEnvironmentsVariable} controlType={ControlType.Environments} allowClear={true}/>
                                </div>) : (<>
                                    <EnvironmentV2MultiSelect environments={this.props.environments} onChange={this.props.onInclusiveEnvironmentsChanged} value={this.props.inclusiveEnvironments} onRemove={this.props.onInclusiveEnvironmentRemoved}/>
                                    <Note>Choose the specific environments under which you want this step to run.</Note>
                                </>)}
                            {!this.isVersionControlled && this.props.hasHiddenEnvironments && (<Callout type={"information"} title={"Insufficient permissions"}>
                                    The {Permission.EnvironmentView} permission is preventing the environment condition type from being changed because you do not have access to an environment that is specifically marked for inclusion.
                                </Callout>)}
                        </div>)}

                    <RadioButton value={EnvironmentOption.Exclude} label="Skip specific environments" disabled={!this.isVersionControlled && this.props.hasHiddenEnvironments}/>
                    {this.props.environmentOption === EnvironmentOption.Exclude && (<div>
                            {this.props.processType === ProcessType.ProcessTemplate ? (<div className={styles.environmentsParameterStyles}>
                                    <ProcessTemplatesParameterSelector onParameterChange={this.props.onExclusiveEnvironmentsParameterChanged} selectedParameter={this.props.exclusiveEnvironmentsVariable} allowClear={true} controlType={ControlType.Environments}/>
                                </div>) : (<>
                                    <EnvironmentV2MultiSelect environments={this.props.environments} onChange={this.props.onExclusiveEnvironmentsChanged} value={this.props.exclusiveEnvironments} onRemove={this.props.onExclusiveEnvironmentRemoved}/>
                                    <Note>Choose the environments for which you want this step to be skipped.</Note>
                                </>)}
                            {!this.isVersionControlled && this.props.hasHiddenEnvironments && (<Callout type={"information"} title={"Insufficient permissions"}>
                                    The {Permission.EnvironmentView} permission is preventing the environment condition type to be changed because you do not have access to an environment that is specifically marked for inclusion.
                                </Callout>)}
                        </div>)}
                </RadioButtonGroup>
            </ExpandableFormSection>);
    }
    private environmentSummary() {
        if (this.props.environmentOption === EnvironmentOption.Include) {
            return this.props.inclusiveEnvironmentsVariable
                ? Summary.summary(<span>
                          This step will only run in environments specified by the parameter <strong>{unbindParameterName(this.props.inclusiveEnvironmentsVariable)}</strong>
                      </span>)
                : Summary.summary(<span>This step will only run in {this.getChipsForEnvironments(this.props.inclusiveEnvironments)}</span>);
        }
        if (this.props.environmentOption === EnvironmentOption.Exclude) {
            return this.props.exclusiveEnvironmentsVariable
                ? Summary.summary(<span>
                          This step will be run in all applicable Lifecycle environments <em>except</em> those specified by the parameter <strong>{unbindParameterName(this.props.exclusiveEnvironmentsVariable)}</strong>
                      </span>)
                : Summary.summary(<span>
                          This step will be run in all applicable Lifecycle environments <em>except</em> {this.getChipsForEnvironments(this.props.exclusiveEnvironments)}
                      </span>);
        }
        return Summary.default("This step will run for all applicable Lifecycle environments");
    }
    private getChipsForEnvironments(environmentKeys: string[]) {
        if (environmentKeys.length === 0) {
            return <em>please select environments</em>;
        }
        return environmentKeys.map((key) => (<Lookup key={key} lookupCollection={this.props.environments} lookupId={key} getIdFromElement={(x) => x.Id} render={(item) => <EnvironmentChip environmentName={item.Name}/>} renderFallback={<ContextualMissingChip key={key} lookupKey={key} type={ChipIcon.Environment}/>}/>));
    }
    static displayName = "Environments";
}
const styles = {
    environmentsParameterStyles: css({
        marginTop: space[12],
    }),
};
export default Environments;
